import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "FilterNozzles",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Wedge Wire Screens",
      standardBadge: "ISO 9044 / Industrial Standards",
      heading: "Stainless Steel Wedge Wire Filter Nozzles",
      lead: "Engineered continuous-slot V-wire and mesh filter nozzles for uniform fluid collection, media retention, and low-pressure-drop liquid-solid distribution in industrial vessels and underdrain systems.",
      secondaryCta: {
        label: "View Wedge Wire Screens",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
        alt: "Precision stainless steel wedge wire filter nozzle assembly with threaded connection",
      },
      cardNote: "Vessel Underdrain & Distribution Media",
      cardLink: {
        label: "Technical Specs",
        to: "/FilterNozzlesPage#specifications",
      },
      specs: [
        { label: "Slot Aperture", value: "0.05 – 2.0 mm" },
        { label: "Standard Alloys", value: "AISI 304 / 316L" },
        { label: "Thread Fittings", value: '1/2" – 2" BSP/NPT' },
        { label: "Flow Direction", value: "Direct & Reverse" },
      ],
    },
    overview: {
      eyebrow: "Vessel Underdrain Components",
      heading: "Uniform Flow Distribution & Resilient Media Retention",
      paragraphs: [
        "In ion exchange columns, mixed-bed demineralizers, carbon filters, and sand bed reactors, underdrain filter nozzles are critical for maintaining uniform hydraulic distribution across false bottom plates. Conventional slotted lateral designs or perforated caps frequently suffer from localized velocity spikes, particle jamming, and resin leakage into downstream manifolds.",
        "MutualFilter manufactures precision stainless steel filter nozzles utilizing continuous-profile resistance-welded wedge wire. The distinctive V-profile wire geometry establishes a two-point particle contact interface at the outer surface that widens inward. This non-clogging geometric aperture guarantees that retained resin beads, activated carbon granules, or granular media cannot bridge or wedge inside the slot during service or backwash cycles.",
        "Constructed in AISI 304, 304L, 316, and 316L stainless steel, our filter nozzles withstand mechanical hydraulic shocks, cyclic water hammer forces, and harsh chemical regenerant streams. Units are supplied with precision-machined BSP or NPT threaded shanks, expanding sleeves, lock nuts, and food-grade or fluoropolymer sealing gaskets tailored to exact vessel plate thicknesses.",
      ],
      keyPoints: [
        "Continuous V-wire slot profile creates two-point contact, preventing particulate wedging and facilitating efficient backwash cycles.",
        "All-welded fusion construction withstands cyclic water hammer loads and thermal fluctuations without media loss.",
        "Configurable with standard BSP/NPT threaded shanks, expanding sleeves, or custom collars to match vessel plate thickness.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
        alt: "Stainless steel continuous-slot wedge wire filter nozzle with threaded shank",
      },
    },
    specTable: {
      heading: "Filter Nozzle Engineering Specifications",
      intro: "Standard dimensional and slot configurations for water treatment vessels, ion exchange demineralizers, and catalyst retention underdrains.",
      columns: [
        { key: "configuration", label: "Nozzle Type", align: "left" },
        { key: "slotRange", label: "Slot Aperture", align: "right" },
        { key: "threadSize", label: "Mounting Connection", align: "left" },
        { key: "headDiameter", label: "Head Diameter", align: "right" },
        { key: "alloys", label: "Standard Material", align: "left" },
      ],
      rows: [
        {
          id: "fn-single-head",
          configuration: "Single-Head Standard Nozzle",
          slotRange: "0.10 – 0.50 mm",
          threadSize: '3/4" or 1" BSP / NPT',
          headDiameter: "50 mm – 70 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-double-head",
          configuration: "Double-Head High-Flow Nozzle",
          slotRange: "0.15 – 1.00 mm",
          threadSize: '1" or 1-1/4" BSP / NPT',
          headDiameter: "50 mm – 90 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-fine-resin",
          configuration: "Fine Resin Trap Nozzle",
          slotRange: "0.05 – 0.20 mm",
          threadSize: '1/2" or 3/4" BSP / NPT',
          headDiameter: "38 mm – 54 mm",
          alloys: "AISI 316L",
        },
        {
          id: "fn-heavy-underdrain",
          configuration: "Heavy-Duty Vessel Strainer Nozzle",
          slotRange: "0.25 – 2.00 mm",
          threadSize: '1-1/2" or 2" BSP / NPT',
          headDiameter: "70 mm – 115 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-expanding-mount",
          configuration: "Expanding Sleeve Plate Nozzle",
          slotRange: "0.10 – 1.00 mm",
          threadSize: "Expansion Sleeve Assembly",
          headDiameter: "50 mm – 80 mm",
          alloys: "AISI 304 / 316L",
        },
      ],
      caption: "Note: Custom stem lengths, slot openings from 0.02 mm to 5.0 mm, and special flange collars are fabricated to vessel engineering drawings.",
    },
    features: {
      heading: "Design Advantages & Construction Standards",
      intro: "Engineered specifically to ensure zero media migration and long service life under continuous industrial operating conditions.",
      items: [
        {
          title: "Non-Clogging V-Wire Profile",
          text: "The inward-enlarging V-shaped slot minimizes hydrodynamic friction and ensures retained grains only contact two outer edges, eliminating bridging and clogging.",
        },
        {
          title: "All-Welded Mechanical Strength",
          text: "Resistance-welded joints between profile wires and support ribs create a rigid monolithic assembly capable of resisting high pressure drops and reverse backwash surge.",
        },
        {
          title: "High Open Area Distribution",
          text: "Precision slot spacing provides generous open area across the nozzle circumference, maintaining balanced flow velocity and minimizing system pressure loss.",
        },
        {
          title: "Versatile Mounting Configurations",
          text: "Threaded shanks in BSP/NPT, expanding rubber sleeves, and custom clamp collars allow secure gas-tight and liquid-tight fastening to any tubesheet thickness.",
        },
      ],
    },
    related: {
      heading: "Related Wedge Wire Screen Solutions",
      intro: "Explore complementary stainless steel wedge wire profiles, screening panels, and cylindrical separation equipment.",
      cards: [
        {
          title: "Flat Panel Screens",
          text: "Rigid flat wedge wire panels for dewatering, vibratory screening, washboxes, and static sieve decks.",
          to: "/FlatPanelScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Flat stainless steel wedge wire screen panel",
          },
          badge: "Flat Screens",
          linkLabel: "View Flat Panels",
        },
        {
          title: "Curved Screens",
          text: "Pre-curved wedge wire panels engineered for parabolic static sieves, run-down screens, and flume separation.",
          to: "/CurvedScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
            alt: "Curved wedge wire screen panel for industrial separation",
          },
          badge: "Curved Media",
          linkLabel: "View Curved Screens",
        },
        {
          title: "Cylindrical Screens",
          text: "Axial and circumferential slot wedge wire cylinders for rotary drums, inline strainers, and pressure vessels.",
          to: "/CylindricalScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Precision cylindrical wedge wire filter screen assemblies",
          },
          badge: "Cylindrical Media",
          linkLabel: "View Cylindrical Screens",
        },
        {
          title: "Screen Tubes",
          text: "High-precision welded screen pipes and wedge wire tubes for intake systems, well screens, and heavy-duty filtration.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Welded stainless steel wedge wire screen tubes",
          },
          badge: "Tubular Elements",
          linkLabel: "View Screen Tubes",
        },
        {
          title: "Sieve Bend Screens",
          text: "Gravity-fed curved screening surfaces for high-capacity solid-liquid separation, mineral sizing, and pulp processing.",
          to: "/SieveBendScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
            alt: "Curved stainless steel sieve bend screen",
          },
          badge: "Sieve Bends",
          linkLabel: "View Sieve Bends",
        },
        {
          title: "Wedge Wire Screen Overview",
          text: "Comprehensive overview of all welded wedge wire screen profiles, slot geometries, and custom fabrication capabilities.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Industrial stainless steel wedge wire screening products overview",
          },
          badge: "Parent Category",
          linkLabel: "View Wedge Wire Screens",
        },
      ],
    },
    cta: {
      id: "filter-nozzles-quote",
      eyebrow: "Precision Nozzle Manufacturing",
      heading: "Request a Technical Quote for Filter Nozzles",
      lead: "Submit your required slot aperture, stem thread specification, head dimensions, and operating media. Our engineering team prepares detailed dimensional drawings and quotes.",
      secondaryLabel: "Browse Wedge Wire Screens",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Material test reports (MTR) and PMI alloy verification available upon request with your order.",
    },
  },
  zh: {
    breadcrumbKey: "FilterNozzles",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "楔形丝筛网",
      standardBadge: "ISO 9044 / 工业通用标准",
      heading: "不锈钢楔形丝滤水帽与工业滤喷嘴",
      lead: "工程级连续缝隙 V 型楔形丝与不锈钢丝网滤水帽，专为工业反应器、树脂交换柱及压力容器提供均匀流体分配、可靠介质截留与低压降运行保障。",
      secondaryCta: {
        label: "查看楔形丝筛网产品",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
        alt: "带螺纹连接管柄的不锈钢精密楔形丝滤水帽与工业滤喷嘴组件",
      },
      cardNote: "容器滤板布水与介质截留滤元",
      cardLink: {
        label: "技术规格",
        to: "/FilterNozzlesPage#specifications",
      },
      specs: [
        { label: "缝隙孔径", value: "0.05 – 2.0 mm" },
        { label: "标准材质", value: "AISI 304 / 316L" },
        { label: "螺纹标准", value: '1/2" – 2" BSP/NPT' },
        { label: "流向形式", value: "正向与反向流动" },
      ],
    },
    overview: {
      eyebrow: "容器底部配水与集水组件",
      heading: "均匀流体分布与零介质泄漏的高可靠保障",
      paragraphs: [
        "在离子交换树脂柱、混床除盐器、活性炭吸附塔及多介质砂滤器中，底部滤板配水滤水帽是保证整塔水流均匀上升或下行分布的核心部件。传统塑料喷嘴或简单冲孔管在长周期运行中易发生水锤破裂、缝隙变形以及树脂颗粒泄漏事故。",
        "互拓公司（MutualFilter）采用精密电阻焊全熔接工艺制造不锈钢楔形丝滤水帽。其独特的连续 V 型截面丝条在迎水面形成两点线接触缝隙，内部截面呈梯形向内放大。这种防堵塞几何截面确保了离子交换树脂颗粒、活性炭粉粒或滤料在过滤与反冲洗过程中绝不嵌塞于缝隙之中。",
        "产品主要采用优质 AISI 304 与 316L 奥氏体不锈钢制造，具备出色的抗水锤冲击韧性与耐化学再生液腐蚀性能。管柄提供标准 BSP、NPT 螺纹、膨胀橡胶套管或平法兰安装结构，可根据容器钢滤板或混凝土多孔板的实际厚度灵活定制配合。",
      ],
      keyPoints: [
        "连续 V 型缝隙两点线接触截面，杜绝颗粒介质卡嵌，实现快速反冲洗自洁。",
        "全熔阻焊坚固结构，耐受周期性水锤冲击与温度波动，确保滤料介质零泄漏。",
        "提供标准 BSP/NPT 螺纹管柄、膨胀套管及定制法兰衬套，精确适配各类容器滤板厚度。",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
        alt: "全焊接不锈钢防堵塞楔形丝滤水帽总成",
      },
    },
    specTable: {
      heading: "滤水帽常用工程规格与配置",
      intro: "适用于工业水处理设备、除盐床及催化剂反应器底部滤板的典型滤水帽规格。所有尺寸均可根据工程图纸生产。",
      columns: [
        { key: "configuration", label: "滤水帽类型", align: "left" },
        { key: "slotRange", label: "缝隙宽度", align: "right" },
        { key: "threadSize", label: "安装螺纹接口", align: "left" },
        { key: "headDiameter", label: "帽头外径", align: "right" },
        { key: "alloys", label: "标准材质", align: "left" },
      ],
      rows: [
        {
          id: "fn-single-head-zh",
          configuration: "单头标准滤水帽",
          slotRange: "0.10 – 0.50 mm",
          threadSize: '3/4" 或 1" BSP / NPT',
          headDiameter: "50 mm – 70 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-double-head-zh",
          configuration: "双头大流量滤水帽",
          slotRange: "0.15 – 1.00 mm",
          threadSize: '1" 或 1-1/4" BSP / NPT',
          headDiameter: "50 mm – 90 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-fine-resin-zh",
          configuration: "细树脂截留型滤水帽",
          slotRange: "0.05 – 0.20 mm",
          threadSize: '1/2" 或 3/4" BSP / NPT',
          headDiameter: "38 mm – 54 mm",
          alloys: "AISI 316L",
        },
        {
          id: "fn-heavy-underdrain-zh",
          configuration: "重载反应器滤网喷嘴",
          slotRange: "0.25 – 2.00 mm",
          threadSize: '1-1/2" 或 2" BSP / NPT',
          headDiameter: "70 mm – 115 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-expanding-mount-zh",
          configuration: "膨胀套筒滤板喷嘴",
          slotRange: "0.10 – 1.00 mm",
          threadSize: "膨胀橡胶套管紧固结构",
          headDiameter: "50 mm – 80 mm",
          alloys: "AISI 304 / 316L",
        },
      ],
      caption: "注：支持管柄长度定制、缝隙宽度 0.02 mm 至 5.0 mm 任意区间定制，以及特殊防松垫片与法兰配件配套加工。",
    },
    features: {
      heading: "结构特点与制造工艺优势",
      intro: "专为严苛工业运行环境下防止滤料泄漏、保障流场均一与长期耐蚀而设计制造。",
      items: [
        {
          title: "自洁防堵 V 型缝隙",
          text: "缝隙截面呈梯形向内逐渐扩大，固相杂质仅接触外表面两点边缘，极低的水流阻力有效促进反冲洗快速脱附。",
        },
        {
          title: "全熔焊接机械强度",
          text: "高频电阻焊熔融结合丝条与内部纵向筋条，整体构件刚性极高，能承受反复的正反向压力交变与水锤冲击。",
        },
        {
          title: "高开孔率均流性能",
          text: "精密丝条排列保证周向均布的高开孔流通截面，使流体沿滤板呈平推流分布，杜绝死区与局部冲蚀偏流。",
        },
        {
          title: "多样化牢固安装形式",
          text: "提供 BSP/NPT 螺纹管柄、膨胀橡胶密封套管及定制卡套法兰，适配各种厚度的金属盲板或预埋复合管板。",
        },
      ],
    },
    related: {
      heading: "相关楔形丝筛网系列产品",
      intro: "探索互拓全系列不锈钢楔形丝筛网板、弧形筛及圆柱形分离过滤产品。",
      cards: [
        {
          title: "平板筛网板",
          text: "高强度平面楔形丝网板，广泛应用于脱水脱介振动筛、洗矿脱渣箱及固定式筛床。",
          to: "/FlatPanelScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "平板不锈钢楔形丝筛网板",
          },
          badge: "平面筛板",
          linkLabel: "查看平板筛详情",
        },
        {
          title: "弧形筛网板",
          text: "精密弧形预弯楔形丝筛网，适用于重力自流脱水弧形筛、走料筛及水力分离槽。",
          to: "/CurvedScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
            alt: "用于工业分离的弧形楔形丝筛板",
          },
          badge: "弧形过滤介质",
          linkLabel: "查看弧形筛详情",
        },
        {
          title: "圆柱形筛网",
          text: "轴向与周向缝隙结构焊接圆柱形筛网，专用于转鼓过滤机、管道过滤器及压力容器滤筒。",
          to: "/CylindricalScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "精密圆柱形楔形丝过滤筛网组件",
          },
          badge: "圆柱形介质",
          linkLabel: "查看圆柱筛详情",
        },
        {
          title: "筛管与管滤元",
          text: "高精度全焊式筛管与连续缠丝滤管，适用于深水井滤管、进水取水头及重工流体过滤。",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "焊接不锈钢楔形丝筛管与滤管",
          },
          badge: "管状滤元",
          linkLabel: "查看筛管详情",
        },
        {
          title: "筛弯网与曲面筛",
          text: "重力式曲面筛网，针对高通量固液分离、矿物分级、淀粉加工及造纸浆料洗涤设计。",
          to: "/SieveBendScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
            alt: "弧形不锈钢重力筛弯网",
          },
          badge: "重力筛弯",
          linkLabel: "查看筛弯网详情",
        },
        {
          title: "楔形丝筛网总览",
          text: "互拓全系列全焊式楔形丝筛条、缝隙结构及工业固液分离过滤产品技术全景。",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "工业不锈钢楔形丝筛网系列产品总览",
          },
          badge: "上级分类",
          linkLabel: "查看楔形丝总览",
        },
      ],
    },
    cta: {
      id: "filter-nozzles-quote",
      eyebrow: "工业滤水帽定制制造",
      heading: "获取滤水帽与喷嘴组件技术方案与报价",
      lead: "请提供所需的缝隙精度、管柄螺纹规格、帽头尺寸及运行工况介质，我们的工程技术团队将为您出具详细的工程图纸与报价方案。",
      secondaryLabel: "返回楔形丝筛网中心",
      secondaryTo: "/WedgeWireScreenPage",
      note: "按订单要求可提供材质检验报告 (MTR) 与 PMI 光谱材质分析。",
    },
  },
  es: {
    breadcrumbKey: "FilterNozzles",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Mallas de Alambre en Cuña",
      standardBadge: "ISO 9044 / Estándares Industriales",
      heading: "Boquillas y Toberas de Filtración en Alambre en Cuña",
      lead: "Toberas y boquillas de filtración en perfiles de alambre en cuña soldados por fusión para distribución uniforme de fluidos, retención estanca de resinas y baja pérdida de carga en recipientes industriales.",
      secondaryCta: {
        label: "Ver Mallas en Cuña",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
        alt: "Boquilla de filtración de alambre en cuña de acero inoxidable con espiga roscada",
      },
      cardNote: "Distribución y Retención en Fondos Falsos",
      cardLink: {
        label: "Especificaciones Técnicas",
        to: "/FilterNozzlesPage#specifications",
      },
      specs: [
        { label: "Abertura de ranura", value: "0,05 – 2,0 mm" },
        { label: "Aleaciones", value: "AISI 304 / 316L" },
        { label: "Roscas", value: '1/2" – 2" BSP/NPT' },
        { label: "Flujo", value: "Directo e inverso" },
      ],
    },
    overview: {
      eyebrow: "Componentes para Fondos de Recipientes",
      heading: "Distribución Homogénea de Flujo y Retención Confiable de Medios",
      paragraphs: [
        "En columnas de intercambio iónico, desmineralizadores de lecho mixto, filtros de carbón activo y reactores de arena, las toberas de filtración son fundamentales para garantizar una distribución hidráulica uniforme a través de las placas inferiores.",
        "MutualFilter fabrica toberas de filtración de precisión en alambre en cuña soldado por resistencia eléctrica continua. El perfil en V genera un contacto en dos puntos en la superficie externa que se amplía hacia el interior, evitando el atrapamiento de granos de resina o carbón durante el funcionamiento y retrolavado.",
        "Fabricadas en acero inoxidable AISI 304 y 316L, resisten golpes de ariete cíclicos y reactivos químicos agresivos. Se suministran con espigas roscadas BSP o NPT, casquillos expansibles o cuellos a medida para adaptarse al espesor de placa requerido.",
      ],
      keyPoints: [
        "El perfil de ranura continua en V crea un contacto en dos puntos que evita el acuñamiento y facilita el retrolavado.",
        "Construcción totalmente soldada por fusión que resiste golpes de ariete cíclicos y variaciones térmicas sin pérdida de medio.",
        "Configurables con vástagos roscados estándar BSP/NPT, manguitos expansibles o cuellos a medida para cualquier espesor de placa.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
        alt: "Tobera de filtración de acero inoxidable de ranura continua",
      },
    },
    specTable: {
      heading: "Especificaciones Técnicas de Boquillas de Filtración",
      intro: "Configuraciones estándar para desmineralizadores, tratamiento de aguas y recipientes de intercambio iónico.",
      columns: [
        { key: "configuration", label: "Tipo de Boquilla", align: "left" },
        { key: "slotRange", label: "Abertura de Ranura", align: "right" },
        { key: "threadSize", label: "Conexión Roscada", align: "left" },
        { key: "headDiameter", label: "Diámetro de Cabezal", align: "right" },
        { key: "alloys", label: "Material Estándar", align: "left" },
      ],
      rows: [
        {
          id: "fn-single-head-es",
          configuration: "Boquilla Estándar Simple",
          slotRange: "0,10 – 0,50 mm",
          threadSize: '3/4" o 1" BSP / NPT',
          headDiameter: "50 mm – 70 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-double-head-es",
          configuration: "Boquilla Doble Gran Caudal",
          slotRange: "0,15 – 1,00 mm",
          threadSize: '1" o 1-1/4" BSP / NPT',
          headDiameter: "50 mm – 90 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-fine-resin-es",
          configuration: "Trampa para Resina Fina",
          slotRange: "0,05 – 0,20 mm",
          threadSize: '1/2" o 3/4" BSP / NPT',
          headDiameter: "38 mm – 54 mm",
          alloys: "AISI 316L",
        },
        {
          id: "fn-heavy-underdrain-es",
          configuration: "Tobera para Servicio Pesado",
          slotRange: "0,25 – 2,00 mm",
          threadSize: '1-1/2" o 2" BSP / NPT',
          headDiameter: "70 mm – 115 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-expanding-mount-es",
          configuration: "Boquilla con Manguito Expansible",
          slotRange: "0,10 – 1,00 mm",
          threadSize: "Fijación por Expansión",
          headDiameter: "50 mm – 80 mm",
          alloys: "AISI 304 / 316L",
        },
      ],
      caption: "Nota: Ranuras de 0,02 mm a 5,0 mm y longitudes de vástago personalizables según plano.",
    },
    features: {
      heading: "Ventajas de Diseño y Estándares de Construcción",
      intro: "Diseñadas específicamente para asegurar una operación continua sin pérdidas de resina ni atascos mecánicos.",
      items: [
        {
          title: "Perfil en V Anti-Colmatación",
          text: "El ensanchamiento interno de la ranura garantiza que las partículas sólidas solo toquen los bordes exteriores, impidiendo taponamientos.",
        },
        {
          title: "Construcción Totalmente Soldada",
          text: "Unión por fusión resistente a cambios cíclicos de presión y sobrepresiones de retrolavado en ambos sentidos.",
        },
        {
          title: "Elevada Área Abierta",
          text: "Excelente permeabilidad perimétrica que optimiza la distribución de velocidad y reduce al mínimo la caída de presión.",
        },
        {
          title: "Conexiones Flexibles",
          text: "Roscas estándar BSP/NPT y manguitos expansibles que permiten montaje estanco en cualquier espesor de chapa.",
        },
      ],
    },
    related: {
      heading: "Mallas de Alambre en Cuña Relacionadas",
      intro: "Conozca otras soluciones de mallas soldadas en perfiles en cuña para separación industrial.",
      cards: [
        {
          title: "Paneles Planos",
          text: "Paneles planos rígidos para escurrido, cribado vibrante y mesas estáticas de separación.",
          to: "/FlatPanelScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Panel plano de alambre en cuña de acero inoxidable",
          },
          badge: "Paneles Planos",
          linkLabel: "Ver Paneles Planos",
        },
        {
          title: "Mallas Curvadas",
          text: "Mallas precurvadas para tamices parabólicos estáticos y canaletas de desagüe.",
          to: "/CurvedScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
            alt: "Malla curvada de alambre en cuña para separación industrial",
          },
          badge: "Medios Curvos",
          linkLabel: "Ver Mallas Curvadas",
        },
        {
          title: "Mallas Cilíndricas",
          text: "Cilindros de ranura axial y circunferencial para tambores rotativos y filtros de tubería.",
          to: "/CylindricalScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Cilindros de alambre en cuña de acero inoxidable",
          },
          badge: "Medios Cilíndricos",
          linkLabel: "Ver Mallas Cilíndricas",
        },
        {
          title: "Tubos Filtrantes",
          text: "Tubos continuos de alambre en cuña para pozos profundos, captación y filtración pesada.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Tubos filtrantes de acero inoxidable en alambre en cuña",
          },
          badge: "Elementos Tubulares",
          linkLabel: "Ver Tubos Filtrantes",
        },
        {
          title: "Tamices Curvos (Sieve Bend)",
          text: "Superficies curvas de separación sólido-líquido por gravedad para minería y pulpa.",
          to: "/SieveBendScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
            alt: "Tamiz curvo de alambre en cuña para servicio por gravedad",
          },
          badge: "Tamices Curvos",
          linkLabel: "Ver Sieve Bends",
        },
        {
          title: "Centro de Mallas en Cuña",
          text: "Visión general de geometrías de alambre en cuña, perfiles soldados y capacidades técnicas.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Gama completa de mallas y perfiles en cuña de acero inoxidable",
          },
          badge: "Categoría Principal",
          linkLabel: "Ver Todas las Mallas",
        },
      ],
    },
    cta: {
      id: "filter-nozzles-quote",
      eyebrow: "Fabricación de Boquillas a Medida",
      heading: "Solicite Cotización para Toberas de Filtración",
      lead: "Indique la abertura de ranura requerida, rosca de vástago y medio filtrante para recibir una propuesta técnica completa.",
      secondaryLabel: "Volver a Mallas en Cuña",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Informes de prueba de material (MTR) y análisis PMI disponibles bajo requerimiento de pedido.",
    },
  },
  de: {
    breadcrumbKey: "FilterNozzles",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Spaltrohr- und Spaltsiebe",
      standardBadge: "ISO 9044 / Industrienormen",
      heading: "Filterdüsen aus Edelstahl mit Spaltrohrprofil",
      lead: "Vollverschweißte V-Draht- und Gewebefilterdüsen für gleichmäßige Flüssigkeitsverteilung, zuverlässige Medienrückhaltung und minimalen Druckverlust in Behälterböden.",
      secondaryCta: {
        label: "Zu den Spaltsieben",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
        alt: "Präzisions-Filterdüse aus Edelstahl mit Gewindestutzen",
      },
      cardNote: "Düsenboden- und Verteilerkomponenten",
      cardLink: {
        label: "Technische Daten",
        to: "/FilterNozzlesPage#specifications",
      },
      specs: [
        { label: "Spaltöffnung", value: "0,05 – 2,0 mm" },
        { label: "Werkstoffe", value: "AISI 304 / 316L" },
        { label: "Gewindegrößen", value: '1/2" – 2" BSP/NPT' },
        { label: "Strömung", value: "Direkt & revers" },
      ],
    },
    overview: {
      eyebrow: "Behälterboden-Komponenten",
      heading: "Gleichmäßige Anströmung und dauerhafte Medienrückhaltung",
      paragraphs: [
        "In Ionenaustauschern, Mischbettfiltern, Aktivkohlekolonnen und Mehrschicht-Sandfiltern sind Düsenbodenelemente entscheidend für eine homogene Durchströmung über die gesamte Bodenfläche.",
        "MutualFilter fertigt Filterdüsen aus kontinuierlich widerstandsgeschweißtem Spaltdraht. Das V-Profil bildet eine Zweipunktauflage an der Außenoberfläche, die sich nach innen trichterförmig erweitert. Dieses verstopfungsfreie Profil verhindert das Festsetzen von Harzkugeln und Granulaten bei Betriebs- und Rückspülphasen.",
        "Ausgeführt in Edelstahl AISI 304 und 316L widerstehen die Düsen zyklischen Druckstößen und chemischen Regeneriermitteln. Erhältlich mit BSP/NPT-Gewindestutzen oder Spreizhülsen passend für jede Bodenstärke.",
      ],
      keyPoints: [
        "Kontinuierliches V-Spaltprofil mit Zweipunktkontakt verhindert Partikeleinklemmung und ermöglicht schnelle Rückspülung.",
        "Vollverschweißte Schmelzschweißung widersteht zyklischen Druckstößen und Temperaturschwankungen ohne Medienverlust.",
        "Lieferbar mit BSP/NPT-Gewindestutzen, Spreizhülsen oder Flanschringen passend zu jeder Behälterbodenstärke.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
        alt: "Vollverschweißte Edelstahl-Filterdüse mit kontinuierlichem V-Spalt",
      },
    },
    specTable: {
      heading: "Filterdüsen Spezifikationen und Bauformen",
      intro: "Standardisierte Abmessungen und Spaltweiten für Wasseraufbereitungsanlagen, Ionenaustauscher und Reaktorböden.",
      columns: [
        { key: "configuration", label: "Düsentyp", align: "left" },
        { key: "slotRange", label: "Spaltöffnung", align: "right" },
        { key: "threadSize", label: "Gewindeanschluss", align: "left" },
        { key: "headDiameter", label: "Kopfdurchmesser", align: "right" },
        { key: "alloys", label: "Standardwerkstoff", align: "left" },
      ],
      rows: [
        {
          id: "fn-single-head-de",
          configuration: "Einkopf-Standarddüse",
          slotRange: "0,10 – 0,50 mm",
          threadSize: '3/4" oder 1" BSP / NPT',
          headDiameter: "50 mm – 70 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-double-head-de",
          configuration: "Doppelkopf-Hochleistungsdüse",
          slotRange: "0,15 – 1,00 mm",
          threadSize: '1" oder 1-1/4" BSP / NPT',
          headDiameter: "50 mm – 90 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-fine-resin-de",
          configuration: "Feinharzdüse",
          slotRange: "0,05 – 0,20 mm",
          threadSize: '1/2" oder 3/4" BSP / NPT',
          headDiameter: "38 mm – 54 mm",
          alloys: "AISI 316L",
        },
        {
          id: "fn-heavy-underdrain-de",
          configuration: "Schwerlastdüse für Reaktorböden",
          slotRange: "0,25 – 2,00 mm",
          threadSize: '1-1/2" oder 2" BSP / NPT',
          headDiameter: "70 mm – 115 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-expanding-mount-de",
          configuration: "Spreizhülsendüse",
          slotRange: "0,10 – 1,00 mm",
          threadSize: "Spreizhülsen-Klemmung",
          headDiameter: "50 mm – 80 mm",
          alloys: "AISI 304 / 316L",
        },
      ],
      caption: "Hinweis: Spaltweiten von 0,02 mm bis 5,0 mm und individuelle Stutzenlängen nach Kundenzeichnung.",
    },
    features: {
      heading: "Konstruktionsvorteile und Fertigungsstandards",
      intro: "Entwickelt für dauerhaften und wartungsfreien Einsatz in anspruchsvollen industriellen Trennverfahren.",
      items: [
        {
          title: "Verstopfungsfreies V-Drahtprofil",
          text: "Die sich nach innen erweiternde Spaltgeometrie schließt Partikeleinklemmungen aus und sorgt für geringen Strömungswiderstand.",
        },
        {
          title: "Vollverschweißte Festigkeit",
          text: "Widerstandsgeschweißte Verbindungen bieten maximale Stabilität gegen Druckstöße und bidirektionale Rückspülkräfte.",
        },
        {
          title: "Hohe freie Siebfläche",
          text: "Eng getaktete Spaltabstände ermöglichen eine hervorragende hydraulische Leistung bei minimalem Druckabfall.",
        },
        {
          title: "Flexible Bodenbefestigung",
          text: "Standardgewinde BSP/NPT und elastische Spreizhülsen gewährleisten dichte Montage in unterschiedlichen Plattenstärken.",
        },
      ],
    },
    related: {
      heading: "Verwandte Spaltsieb-Lösungen",
      intro: "Entdecken Sie weitere Spaltsiebkonstruktionen aus Edelstahl für die industrielle Fest-Flüssig-Trennung.",
      cards: [
        {
          title: "Flachsieb-Paneele",
          text: "Stabile Flachsiebe für Entwässerungsrinnen, Vibrationssiebe und stationäre Siebdecks.",
          to: "/FlatPanelScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Flaches Spaltsiebpaneel aus Edelstahl",
          },
          badge: "Flachpaneele",
          linkLabel: "Flachsiebe ansehen",
        },
        {
          title: "Bogensiebe",
          text: "Vorgekrümmte Spaltrohrpaneele für statische Bogensiebe und Entwässerungsrinnen.",
          to: "/CurvedScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
            alt: "Gebogenes Spaltsiebpaneel für industrielle Trenntechnik",
          },
          badge: "Gekrümmte Medien",
          linkLabel: "Bogensiebe ansehen",
        },
        {
          title: "Zylindersiebe",
          text: "Spaltrohre mit axialen und radialen Spalten für Trommelsiebe und Rohrleitungsfilter.",
          to: "/CylindricalScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Zylindrische Spaltsiebfilterelemente aus Edelstahl",
          },
          badge: "Zylindrische Medien",
          linkLabel: "Zylindersiebe ansehen",
        },
        {
          title: "Filterrohre & Siebrohre",
          text: "Kontinuierlich geschweißte Spaltrohre für Brunnenbau, Ansaugkörbe und Hochleistungsfiltration.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Geschweißte Spaltrohre und Filterrohre aus Edelstahl",
          },
          badge: "Rohrelemente",
          linkLabel: "Filterrohre ansehen",
        },
        {
          title: "Sieve Bend Siebbögen",
          text: "Schwerkraftbetriebene gekrümmte Spaltsiebe für mineralische Aufbereitung und Zellstoffgewinnung.",
          to: "/SieveBendScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
            alt: "Schwerkraft-Siebbogen aus Edelstahl",
          },
          badge: "Siebbögen",
          linkLabel: "Sieve Bends ansehen",
        },
        {
          title: "Spaltsiebe Übersicht",
          text: "Gesamtübersicht aller verschweißten Spaltrohr- und Spaltsiebprofile von MutualFilter.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Übersicht über industrielle Spaltsiebe und Filterdüsen aus Edelstahl",
          },
          badge: "Hauptkategorie",
          linkLabel: "Alle Spaltsiebe ansehen",
        },
      ],
    },
    cta: {
      id: "filter-nozzles-quote",
      eyebrow: "Individuelle Düsenfertigung",
      heading: "Technisches Angebot für Filterdüsen anfordern",
      lead: "Nennen Sie uns Ihre Spaltweite, Anschlussart und Medienbedingungen für ein maßgeschneidertes Angebot.",
      secondaryLabel: "Zu den Spaltsieben",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Werkstoffanalysen auf Wunsch bei Bestellung erhältlich.",
    },
  },
  fr: {
    breadcrumbKey: "FilterNozzles",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Grilles en Fil en Coin",
      standardBadge: "ISO 9044 / Normes Industrielles",
      heading: "Crépines et Buses de Filtration en Fil en Coin",
      lead: "Crépines et buses de filtration mécano-soudées en fil en coin pour une distribution homogène des flux, la rétention absolue des résines et une faible perte de charge en cuves industrielles.",
      secondaryCta: {
        label: "Voir les Grilles en Fil en Coin",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
        alt: "Crépine de filtration en fil en coin inox avec tige filetée",
      },
      cardNote: "Composants de Planchers et Rétention",
      cardLink: {
        label: "Spécifications Techniques",
        to: "/FilterNozzlesPage#specifications",
      },
      specs: [
        { label: "Ouverture de fente", value: "0,05 – 2,0 mm" },
        { label: "Alliages", value: "AISI 304 / 316L" },
        { label: "Raccordements", value: '1/2" – 2" BSP/NPT' },
        { label: "Sens de flux", value: "Direct et inverse" },
      ],
    },
    overview: {
      eyebrow: "Composants de Planchers de Filtration",
      heading: "Distribution Uniforme et Rétention Fiable des Milieux Granulaires",
      paragraphs: [
        "Dans les colonnes d'échange d'ions, lits mélangés, filtres à charbon actif et réacteurs de déminéralisation, les crépines de plancher assurent une distribution hydraulique homogène sur toute la surface de la plaque de fond.",
        "MutualFilter fabrique des buses et crépines de filtration en fil triangulaire en coin soudé par résistance électrique. Le profil en V procure un contact en deux points sur la face extérieure qui s'élargit vers l'intérieur, empêchant l'incrustation des billes de résine et particules de charbon en filtration comme en détassage.",
        "Réalisées en acier inoxydable AISI 304 et 316L, nos crépines supportent les coups de bélier répétés et les solutions régénérantes agressives. Elles sont fournies avec tiges filetées BSP/NPT ou manchons extensibles adaptés à chaque plancher.",
      ],
      keyPoints: [
        "Profil de fente continu en V assurant un contact en deux points qui évite le coincement des particules et facilite le rétro-lavage.",
        "Construction mécano-soudée par fusion résistant aux coups de bélier et aux variations thermiques sans fuite de média.",
        "Configurables avec tiges filetées BSP/NPT, manchons extensibles ou collerettes sur mesure adaptées à chaque plancher.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
        alt: "Crépine inox mécano-soudée en fil en coin anti-colmatage",
      },
    },
    specTable: {
      heading: "Spécifications Techniques des Crépines de Filtration",
      intro: "Configurations standards pour adoucisseurs, colonnes de déminéralisation et réacteurs industriels.",
      columns: [
        { key: "configuration", label: "Type de Crépine", align: "left" },
        { key: "slotRange", label: "Ouverture de Fente", align: "right" },
        { key: "threadSize", label: "Raccordement", align: "left" },
        { key: "headDiameter", label: "Diamètre Tête", align: "right" },
        { key: "alloys", label: "Matériau Standard", align: "left" },
      ],
      rows: [
        {
          id: "fn-single-head-fr",
          configuration: "Crépine Simple Tête Standard",
          slotRange: "0,10 – 0,50 mm",
          threadSize: '3/4" ou 1" BSP / NPT',
          headDiameter: "50 mm – 70 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-double-head-fr",
          configuration: "Crépine Double Tête Gros Débit",
          slotRange: "0,15 – 1,00 mm",
          threadSize: '1" ou 1-1/4" BSP / NPT',
          headDiameter: "50 mm – 90 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-fine-resin-fr",
          configuration: "Crépine Piège à Résines Fines",
          slotRange: "0,05 – 0,20 mm",
          threadSize: '1/2" ou 3/4" BSP / NPT',
          headDiameter: "38 mm – 54 mm",
          alloys: "AISI 316L",
        },
        {
          id: "fn-heavy-underdrain-fr",
          configuration: "Crépine Renforcée pour Réacteurs",
          slotRange: "0,25 – 2,00 mm",
          threadSize: '1-1/2" ou 2" BSP / NPT',
          headDiameter: "70 mm – 115 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-expanding-mount-fr",
          configuration: "Crépine à Manchon Expansible",
          slotRange: "0,10 – 1,00 mm",
          threadSize: "Fixation par Expansion",
          headDiameter: "50 mm – 80 mm",
          alloys: "AISI 304 / 316L",
        },
      ],
      caption: "Note : Fentes de 0,02 mm à 5,0 mm et longueurs de tiges adaptables sur plan technique.",
    },
    features: {
      heading: "Avantages de Conception et Finitions",
      intro: "Conçues pour garantir une étanchéité absolue aux médias filtrants et une longévité élevée.",
      items: [
        {
          title: "Profil en V Anti-Colmatage",
          text: "L'élargissement intérieur de la fente assure un contact minimal à deux points évitant tout blocage de particules.",
        },
        {
          title: "Résistance Mécano-Soudée",
          text: "Soudure par résistance créant une structure monobloc résistante aux coups de bélier et pressions différentielles.",
        },
        {
          title: "Grande Surface Ouverte",
          text: "L'espacement maîtrisé des fils délivre une distribution homogène du fluide et minimise les pertes de charge.",
        },
        {
          title: "Raccordements Polyvalents",
          text: "Tiges filetées BSP/NPT et manchons expansibles assurant un montage étanche sur tous types de planchers.",
        },
      ],
    },
    related: {
      heading: "Grilles en Fil en Coin Complémentaires",
      intro: "Découvrez notre gamme complète d'éléments filtrants en fil triangulaire inox pour l'industrie.",
      cards: [
        {
          title: "Panneaux Plans",
          text: "Panneaux plans rigides pour égouttage, criblage vibrant et lits fixes de séparation.",
          to: "/FlatPanelScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Panneau plan en fil en coin inox",
          },
          badge: "Panneaux Plans",
          linkLabel: "Voir les Panneaux",
        },
        {
          title: "Grilles Incurvées",
          text: "Panneaux pré-cintrés pour tamis paraboliques statiques et goulottes de dégrillage.",
          to: "/CurvedScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
            alt: "Grille incurvée en fil en coin pour séparation industrielle",
          },
          badge: "Milieux Courbes",
          linkLabel: "Voir les Grilles Incurvées",
        },
        {
          title: "Grilles Cylindriques",
          text: "Cylindres à fente axiale ou circonférentielle pour tambours rotatifs et filtres de tuyauterie.",
          to: "/CylindricalScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Cylindres en fil en coin de précision en acier inoxydable",
          },
          badge: "Milieux Cylindriques",
          linkLabel: "Voir les Grilles Cylindriques",
        },
        {
          title: "Tubes Filtrants",
          text: "Tubes continus en fil en coin pour captage d'eau, puits profonds et filtration lourde.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Tubes filtrants soudés en fil en coin inox",
          },
          badge: "Éléments Tubulaires",
          linkLabel: "Voir les Tubes Filtrants",
        },
        {
          title: "Tamis Courbes (Sieve Bend)",
          text: "Surfaces courbes par gravité pour séparation liquide-solide à fort débit dans les mines et la pâte à papier.",
          to: "/SieveBendScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
            alt: "Tamis courbe de séparation solide-liquide",
          },
          badge: "Tamis Courbes",
          linkLabel: "Voir les Sieve Bends",
        },
        {
          title: "Gamme Grilles en Fil en Coin",
          text: "Vue d'ensemble de nos profils, fentes de filtration et réalisations mécano-soudées sur mesure.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Vue d'ensemble des grilles industrielles en fil en coin inox",
          },
          badge: "Catégorie Parente",
          linkLabel: "Toutes les Grilles",
        },
      ],
    },
    cta: {
      id: "filter-nozzles-quote",
      eyebrow: "Fabrication de Crépines sur Mesure",
      heading: "Demandez une Étude et un Devis pour Crépines de Filtration",
      lead: "Précisez l'ouverture de fente, la tige filetée et les conditions d'exploitation pour recevoir une proposition adaptée.",
      secondaryLabel: "Consulter les Grilles en Fil en Coin",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Certificats matière (MTR) et contrôle PMI disponibles sur demande lors de votre commande.",
    },
  },
  pt: {
    breadcrumbKey: "FilterNozzles",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Telas de Fio Cunha",
      standardBadge: "ISO 9044 / Normas Industriais",
      heading: "Bocais e Crepinas de Filtragem em Fio Cunha",
      lead: "Bocais e crepinas de filtragem soldadas por fusão em perfil de fio cunha para distribuição uniforme de líquidos, retenção segura de resinas e baixa perda de carga em vasos industriais.",
      secondaryCta: {
        label: "Ver Telas de Fio Cunha",
        to: "/WedgeWireScreenPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
        alt: "Bocal de filtragem em fio cunha de aço inoxidável com haste roscada",
      },
      cardNote: "Distribuição e Retenção em Fundos Falsos",
      cardLink: {
        label: "Especificações Técnicas",
        to: "/FilterNozzlesPage#specifications",
      },
      specs: [
        { label: "Abertura de fenda", value: "0,05 – 2,0 mm" },
        { label: "Ligas de aço", value: "AISI 304 / 316L" },
        { label: "Padrões de rosca", value: '1/2" – 2" BSP/NPT' },
        { label: "Direção do fluxo", value: "Direto e reverso" },
      ],
    },
    overview: {
      eyebrow: "Componentes para Fundos de Vasos",
      heading: "Distribuição Hidráulica Homogênea e Retenção Firme de Leito",
      paragraphs: [
        "Em colunas de troca iônica, desmineralizadores de leito misto, filtros de carvão ativado e vasos de areia, os bocais e crepinas de filtragem são essenciais para assegurar uma distribuição homogênea do fluxo através do fundo falso.",
        "A MutualFilter fabrica crepinas e bocais de filtragem em fio cunha com solda contínua por resistência. O perfil em V proporciona contato em dois pontos na face externa que se abre para o interior, impedindo o aprisionamento de partículas de resina ou carvão durante a operação e retrolavagem.",
        "Produzidas em aços inoxidáveis AISI 304 e 316L, resistem a golpes de aríete cíclicos e agentes químicos regenerantes. Disponíveis com hastes roscadas BSP/NPT ou buchas expansíveis para qualquer espessura de chapa.",
      ],
      keyPoints: [
        "Perfil de fenda contínua em V com contato em dois pontos, prevenindo o entupimento de partículas e facilitando a retrolavagem.",
        "Construção totalmente soldada por fusão que resiste a golpes de aríete cíclicos e oscilações térmicas sem perda de leito.",
        "Disponíveis com hastes roscadas padrão BSP/NPT, buchas expansíveis ou colarinhos sob medida para qualquer espessura de placa.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
        alt: "Crepina em fio cunha de aço inoxidável com fenda contínua anti-entupimento",
      },
    },
    specTable: {
      heading: "Especificações Técnicas de Bocais de Filtragem",
      intro: "Dimensões e aberturas habituais para sistemas de tratamento de água, leitos de troca iônica e retenção de catalisadores.",
      columns: [
        { key: "configuration", label: "Tipo de Bocal", align: "left" },
        { key: "slotRange", label: "Abertura de Fenda", align: "right" },
        { key: "threadSize", label: "Rosca de Montagem", align: "left" },
        { key: "headDiameter", label: "Diâmetro Cabeça", align: "right" },
        { key: "alloys", label: "Material Padrão", align: "left" },
      ],
      rows: [
        {
          id: "fn-single-head-pt",
          configuration: "Bocal Padrão Simples",
          slotRange: "0,10 – 0,50 mm",
          threadSize: '3/4" ou 1" BSP / NPT',
          headDiameter: "50 mm – 70 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-double-head-pt",
          configuration: "Bocal Duplo Alta Vazão",
          slotRange: "0,15 – 1,00 mm",
          threadSize: '1" ou 1-1/4" BSP / NPT',
          headDiameter: "50 mm – 90 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-fine-resin-pt",
          configuration: "Retentor de Resina Fina",
          slotRange: "0,05 – 0,20 mm",
          threadSize: '1/2" ou 3/4" BSP / NPT',
          headDiameter: "38 mm – 54 mm",
          alloys: "AISI 316L",
        },
        {
          id: "fn-heavy-underdrain-pt",
          configuration: "Bocal para Serviço Pesado",
          slotRange: "0,25 – 2,00 mm",
          threadSize: '1-1/2" ou 2" BSP / NPT',
          headDiameter: "70 mm – 115 mm",
          alloys: "AISI 304 / 316L",
        },
        {
          id: "fn-expanding-mount-pt",
          configuration: "Bocal com Bucha Expansível",
          slotRange: "0,10 – 1,00 mm",
          threadSize: "Fixação por Expansão",
          headDiameter: "50 mm – 80 mm",
          alloys: "AISI 304 / 316L",
        },
      ],
      caption: "Nota: Fendas de 0,02 mm a 5,0 mm e comprimentos de haste sob encomenda conforme projeto.",
    },
    features: {
      heading: "Diferenciais Técnicos e Acabamento",
      intro: "Projetados para garantir retenção absoluta do meio filtrante e alta resistência a pulsações de pressão.",
      items: [
        {
          title: "Perfil em V Não-Entupível",
          text: "A abertura interna progressiva da fenda minimiza o atrito e impede a fixação de partículas nas paredes.",
        },
        {
          title: "União Totalmente Soldada",
          text: "Resistência soldada de alta integridade capaz de suportar impulsos de retrolavagem bidirecionais sem deformação.",
        },
        {
          title: "Excelente Área Aberta",
          text: "Espaçamento perimétrico de alta precisão que mantém baixa perda de carga e vazão equilibrada em todo o leito.",
        },
        {
          title: "Montagem Confiável",
          text: "Hastes roscadas BSP/NPT e buchas de expansão adequadas para fixação estanque em chapas de variadas espessuras.",
        },
      ],
    },
    related: {
      heading: "Telas de Fio Cunha Relacionadas",
      intro: "Conheça outras soluções em telas metálicas soldadas em perfil de cunha para separação industrial.",
      cards: [
        {
          title: "Painéis Planos",
          text: "Painéis planos rígidos para desaguamento, peneiramento vibratório e calhas estáticas.",
          to: "/FlatPanelScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-fla-ac5b6f.png",
            alt: "Painel plano de tela de fio cunha em aço inoxidável",
          },
          badge: "Painéis Planos",
          linkLabel: "Ver Painéis Planos",
        },
        {
          title: "Telas Curvadas",
          text: "Telas pré-curvadas para peneiras parabólicas estáticas e calhas de desaguamento.",
          to: "/CurvedScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
            alt: "Tela curvada em fio cunha para separação industrial",
          },
          badge: "Meios Curvos",
          linkLabel: "Ver Telas Curvadas",
        },
        {
          title: "Telas Cilíndricas",
          text: "Cilindros de fenda axial e circunferencial para tambores rotativos e filtros de linha.",
          to: "/CylindricalScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
            alt: "Cilindros de fio cunha em aço inoxidável",
          },
          badge: "Meios Cilíndricos",
          linkLabel: "Ver Telas Cilíndricas",
        },
        {
          title: "Tubos de Filtragem",
          text: "Tubos soldados de fio cunha para poços profundos, captação e filtragem pesada.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Tubos de tela soldada de fio cunha em aço inoxidável",
          },
          badge: "Elementos Tubulares",
          linkLabel: "Ver Tubos Filtrantes",
        },
        {
          title: "Telas Curvas Sieve Bend",
          text: "Superfícies curvas de alimentação por gravidade para mineração, papel e celulose.",
          to: "/SieveBendScreensPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-33310b.png",
            alt: "Tela curva de fio cunha para separação gravimétrica",
          },
          badge: "Telas Curvas",
          linkLabel: "Ver Sieve Bends",
        },
        {
          title: "Visão Geral de Telas Cunha",
          text: "Catálogo completo de perfis em cunha soldados, geometrias de fenda e customizações.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-bbbfab.png",
            alt: "Visão geral de telas de fio cunha e crepinas em aço inoxidável",
          },
          badge: "Categoria Principal",
          linkLabel: "Ver Todas as Telas",
        },
      ],
    },
    cta: {
      id: "filter-nozzles-quote",
      eyebrow: "Fabricação Sob Medida de Crepinas",
      heading: "Solicite uma Cotação Técnica para Bocais de Filtragem",
      lead: "Envie as especificações de fenda, rosca da haste e fluido operacional para receber uma proposta técnica completa.",
      secondaryLabel: "Ver Telas de Fio Cunha",
      secondaryTo: "/WedgeWireScreenPage",
      note: "Relatórios de teste de material (MTR) e verificação de liga PMI disponíveis mediante solicitação no pedido.",
    },
  },
};
